<script lang="ts">
	import { getClients } from "@api/getClients.js";
	import sendWebMessageMutation from "@api/mutations/sendWebMessage.graphql";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { WebMessageInput } from "@core/schema/WebMessage.js";
	import { WebMessageType } from "@core/schema/WebMessageType.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { cleanPhoneNumber } from "@core/utils/cleanPhoneNumber.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { formatPhoneNumberOnInput } from "@core/utils/svelte/formatPhoneNumberOnInput.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./ContactForm.translations.json" with { type: "json" };
	import Button from "./common/Button.svelte";
	import { ButtonSize } from "./common/ButtonSize.js";
	import { ButtonVariant } from "./common/ButtonVariant.js";
	import Formatted from "./common/Formatted.svelte";
	import Modal from "./common/Modal.svelte";
	import Form from "./form/Form.svelte";
	import Input from "./form/Input.svelte";
	import Select from "./form/Select.svelte";
	import Textarea from "./form/Textarea.svelte";

	const globalSettings = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);

	let showModal = $state(false);
	let phoneValue = $state("");
	let businessTypeValue = $state<string | undefined>(undefined);

	const { type }: { type?: WebMessageType } = $props();

	const businessTypeOptions = [
		"cafe",
		"bistro",
		"restaurant",
		"office",
		"hotel",
		"eshop",
	] satisfies (keyof typeof translations)[];
	const otherBusinessTypeKey = "other";

	const send = createPromiseWithStatus(async (formData: FormData) => {
		if (!navigator.onLine) {
			throw new Error(translate("offlineError"));
		}
		const now = new Date().toISOString();

		const { graphQL } = await getClients(globalSettings);
		await graphQL.mutate<{ input: WebMessageInput }>({
			mutation: sendWebMessageMutation,
			variables: {
				input: {
					gdprApprovedAt: now,
					name: formData.get("name") as string,
					phoneNumber: cleanPhoneNumber(phoneValue),
					email: formData.get("email") as string,
					message: formData.get("message") as string,
					type: type ?? WebMessageType.Contact,
					locale: tenant.locale,
					sentFromUrl: location.href,
					...(type === WebMessageType.Wholesale && {
						city: formData.get("city") as string,
						businessType: (formData.get("businessType") === otherBusinessTypeKey
							? formData.get("businessTypeCustom")
							: formData.get("businessType")) as string,
					}),
				},
			},
		});
	});

	async function submitContactForm(_event: SubmitEvent, form: HTMLFormElement): Promise<void> {
		const formData = new FormData(form);
		try {
			await send.call(formData);
			form.reset();
		} finally {
			showModal = true;
		}
	}

	const loading = $derived(send.value.status === "loading");
	const isError = $derived(send.value.status === "error");
	const modalMessage = $derived.by(() => {
		if (send.value.status === "success") {
			return translate("successMessage");
		}
		if (send.value.status === "error") {
			return translate("errorMessage", { error: String(send.value.error) });
		}
		return "";
	});
</script>

<Form class="flex flex-col gap-2" onsubmit={submitContactForm} enableRecaptcha>
	{#if type === WebMessageType.Wholesale}
		<div
			class={[
				"grid gap-2 grid-cols-1",
				otherBusinessTypeKey === businessTypeValue ? "sm:grid-cols-2" : "sm:grid-cols-1",
			]}
		>
			<Select required name="businessType" placeholder={translate("businessType")} bind:value={businessTypeValue}>
				{#each businessTypeOptions as businessType (businessType)}
					<option value={translate(businessType)}>{translate(businessType)}</option>
				{/each}
				<option value={otherBusinessTypeKey}>{translate("otherBusinessType")}</option>
			</Select>
			{#if otherBusinessTypeKey === businessTypeValue}
				<Input required placeholder={translate("businessTypeCustom")} name="businessTypeCustom" />
			{/if}
		</div>
		<Input required placeholder={translate("city")} name="city" />
	{/if}
	<Input required placeholder={translate("name")} autocomplete="name" name="name" />
	<Input required placeholder={translate("email")} autocomplete="email" name="email" type="email" />
	<Input
		required
		placeholder={translate("phone")}
		name="phone"
		autocomplete="tel"
		type="tel"
		bind:value={phoneValue}
		oninput={formatPhoneNumberOnInput}
		maxlength={20}
	/>
	<Textarea required placeholder={translate("message")} autocomplete="off" name="message" class="h-50" />

	<div class="flex justify-center font-semibold text-3.5 mt-8">
		<Button size={ButtonSize.Medium} variant={ButtonVariant.Dark} type="submit" {loading}>
			<Formatted text={loading ? translate("sending") : translate("sendMessage")} />
		</Button>
	</div>
	<div class="text-fcrgray text-3.5 flex flex-col items-center my-6">
		<div>{translate("gdprConsentPrefix")}</div>
		<a href={getGdprPath(tenant)} target="_blank" class="hover:underline hover:text-fcrbrown">
			{translate("gdprLinkText")}
		</a>
	</div>
</Form>

<Modal bind:show={showModal} heading={translate(isError ? "errorHeading" : "successHeading")}>
	<p class="text-fcrgray text-sm/6 md:text-base max-w-127 mt-2">
		<Formatted text={modalMessage} />
	</p>
</Modal>
